import {
  CARD_PADDING_PX,
  COLORS,
  CONTENT_GAP_PX,
  FOOTER_RESERVE,
  LINE,
  TYPE,
  cardSegments,
  columnWidth,
  columnX,
  count,
  createDocument,
  drawCard,
  drawCardHeader,
  drawCardSegments,
  drawDivider,
  drawTable,
  drawText,
  renderEntityCard,
  renderFooter,
  renderHeader,
  renderStatCards,
  setFont,
  tableHeight,
  textHeight,
} from "~/components/utils/pdfLayout";
import pluralize from "pluralize";

// Mirrors the metric modifier classes SubmissionSuccess.jsx picks by title.
const METRIC_COLORS = {
  Conforming: COLORS.green,
  "Non-Conforming": COLORS.red,
  "Claims with Warnings": COLORS.orange,
};

// One labelled progress bar per metric, coloured by title like the page's modifier classes.
function renderMetricsCard(layout, metrics, x, width) {
  const { doc, u } = layout;
  const padding = u(CARD_PADDING_PX);
  const barWidth = width - padding * 2;

  let y = layout.y + drawCardHeader(layout, "METRICS", x, layout.y, width) + padding;

  (metrics || []).forEach((metric, index) => {
    const fill = METRIC_COLORS[metric.title] || COLORS.subtitle;
    const percentage = Number(metric.percentage) || 0;

    if (index > 0) y += u(16);

    y += drawText(layout, TYPE.bodyStrong, metric.title || "", x + padding, y);
    y += drawText(layout, TYPE.description, metric.description || "", x + padding, y, {
      width: barWidth,
      maxLines: 2,
    });

    const barHeight = u(6);
    const radius = u(3);
    doc.setFillColor(...COLORS.border);
    doc.roundedRect(x + padding, y, barWidth, barHeight, radius, radius, "F");

    if (percentage > 0) {
      doc.setFillColor(...fill);
      doc.roundedRect(
        x + padding,
        y,
        (barWidth * Math.min(percentage, 100)) / 100,
        barHeight,
        radius,
        radius,
        "F"
      );
    }

    y += barHeight + u(8);

    drawText(
      layout,
      { ...TYPE.statValueSmall, color: COLORS.subtitle },
      count(metric.count),
      x + padding,
      y
    );
    y += drawText(
      layout,
      { ...TYPE.statValueSmall, color: fill },
      `${percentage}%`,
      x + width - padding,
      y,
      {
        align: "right",
      }
    );
  });

  return y + padding - layout.y;
}

function drawWarningIcon(layout, x, y) {
  const { doc, u } = layout;

  const size = u(16);
  const centerX = x + size / 2;

  doc.setDrawColor(255, 104, 31);
  doc.setFillColor(255, 104, 31);
  doc.setLineWidth(u(1.2));

  doc.triangle(centerX, y, x, y + size, x + size, y + size, "S");

  doc.line(centerX, y + u(5), centerX, y + u(10.5));

  doc.circle(centerX, y + u(13), u(0.8), "F");
}

function renderWarningBanner(layout, { count }) {
  const { doc, margin, contentWidth, u } = layout;

  const maxWidth = u(706);
  const width = Math.min(contentWidth, maxWidth);

  // Center the banner inside the normal content area
  const x = margin + (contentWidth - width) / 2;

  const padding = u(16);
  const gap = u(16);

  // CSS box is 20px, while the actual drawn icon is slightly smaller
  const iconBox = u(20);
  const iconSize = u(16);

  const radius = u(8);

  const top = layout.y - u(13);

  setFont(layout, TYPE.body);

  const text = `This submission contained ${count} ${pluralize("row", count)} related to invalid 340B IDs and/or 340B IDs that you do not have access to view. You will not be able to view data related to these 340B IDs. If you think you should have access to these 340B IDs, please contact your administrator.`;

  const textWidth = width - padding * 2 - iconBox - gap;

  const lines = doc.splitTextToSize(text || "", textWidth);

  const lineHeight = u(TYPE.body.px * LINE);

  const textHeight = lines.length * lineHeight;

  const contentHeight = Math.max(iconBox, textHeight);

  const height = padding * 2 + contentHeight;

  // Background
  doc.setFillColor(255, 221, 204);

  doc.roundedRect(x, top, width, height, radius, radius, "F");

  // Center the 16px icon inside its 20px box
  const iconX = x + padding + (iconBox - iconSize) / 2;

  const iconY = top + padding + (contentHeight - iconSize) / 2;

  drawWarningIcon(layout, iconX, iconY);

  // Text
  setFont(layout, TYPE.body);
  doc.setTextColor(0, 0, 0);

  const textX = x + padding + iconBox + gap;

  const textY = top + padding + u(TYPE.body.px);

  doc.text(lines, textX, textY, {
    lineHeightFactor: LINE,
  });

  layout.y = top + height + u(27);
}

/*
A `Covered Entities` / `Manufacturers` section: a labelled count over a list. The list can't
break across pages, so `maxRows` trades the tail of it for a count of what was left off — the
page does the same thing with a 140px scroll box, and every entity is itemised further down.
*/
function drawListSection(layout, { title, total, x, y, width, rows, columns = 1, maxRows }) {
  const { u } = layout;
  const padding = u(CARD_PADDING_PX);
  const capped = maxRows != null && rows.length > maxRows;
  const items = capped ? rows.slice(0, Math.max(maxRows - 1, 1)) : rows;
  const hidden = rows.length - items.length;
  let cursor = y + padding;

  drawText(layout, TYPE.bodyStrong, title, x + padding, cursor);
  drawText(
    layout,
    { ...TYPE.statValueSmall, color: COLORS.title },
    count(total),
    x + width - padding,
    cursor,
    {
      align: "right",
    }
  );

  cursor += textHeight(layout, TYPE.bodyStrong, 1) + u(8);

  const listWidth = (width - padding * 2 - u(CONTENT_GAP_PX) * (columns - 1)) / columns;
  const lineHeight = textHeight(layout, TYPE.listItem, 1) + u(4);

  const rowX = (index) => x + padding + (index % columns) * (listWidth + u(CONTENT_GAP_PX));
  const rowY = (index) => cursor + Math.floor(index / columns) * lineHeight;

  items.forEach(([primary, secondary], index) => {
    drawText(layout, TYPE.listItem, primary, rowX(index), rowY(index));
    if (secondary) {
      drawText(layout, TYPE.listItem, secondary, rowX(index) + listWidth, rowY(index), {
        align: "right",
      });
    }
  });

  if (hidden) {
    const muted = { ...TYPE.listItem, color: COLORS.subtitle };
    drawText(layout, muted, `+${count(hidden)} more`, rowX(items.length), rowY(items.length));
  }

  const listRows = Math.ceil((items.length + (hidden ? 1 : 0)) / columns);

  return (
    padding * 2 +
    textHeight(layout, TYPE.bodyStrong, 1) +
    u(8) +
    Math.max(listRows * lineHeight - u(4), 0)
  );
}

// How many list rows the two sections of the entities card can share before the card would
// run past the footer. Neither section can break, so they are capped rather than clipped.
function listBudget(layout, y) {
  const { u } = layout;
  const lineHeight = textHeight(layout, TYPE.listItem, 1) + u(4);
  const chrome =
    u(CARD_PADDING_PX * 6 + 8 * 2) +
    textHeight(layout, TYPE.cardTitle, 1) +
    textHeight(layout, TYPE.bodyStrong, 2);

  return Math.max(Math.floor((layout.pageHeight - FOOTER_RESERVE - y - chrome) / lineHeight), 4);
}

// Covered entity list (name + 340B ID) above a two-column manufacturer list.
function renderEntitiesCard(layout, summary, x, width) {
  const { doc } = layout;
  const entities = summary.entities || [];
  const manufacturers = summary.manufacturers || [];

  // The manufacturers keep their rows where they can, and the entities take what is left.
  const budget = listBudget(layout, layout.y);
  const entityRows = Math.max(budget - Math.ceil(manufacturers.length / 2), Math.ceil(budget / 2));

  let y = layout.y + drawCardHeader(layout, "COVERED ENTITIES & MANUFACTURERS", x, layout.y, width);

  y += drawListSection(layout, {
    title: "Covered Entities",
    total: entities.length,
    x,
    y,
    width,
    rows: entities.map((entity) => [entity.name || "", entity.id_340b || ""]),
    maxRows: entityRows,
  });

  doc.setDrawColor(...COLORS.border);
  doc.line(x, y, x + width, y);

  y += drawListSection(layout, {
    title: "Manufacturers",
    total: manufacturers.length,
    x,
    y,
    width,
    rows: manufacturers.map((manufacturer) => [manufacturer]),
    columns: 2,
    maxRows: Math.max(budget - Math.min(entities.length, entityRows), 2) * 2,
  });

  return y - layout.y;
}

// The page shows Metrics and Covered Entities side by side, both stretched to the taller card.
function renderValidationSummary(layout, summary) {
  const width = columnWidth(layout, 2);
  const metricsX = columnX(layout, 0, width);
  const entitiesX = columnX(layout, 1, width);

  const startY = layout.y;
  const metricsHeight = renderMetricsCard(layout, summary.metrics, metricsX, width);
  const entitiesHeight = renderEntitiesCard(layout, summary, entitiesX, width);
  const height = Math.max(metricsHeight, entitiesHeight);

  drawCard(layout, metricsX, startY, width, height);
  drawCard(layout, entitiesX, startY, width, height);

  layout.y = startY + height;
}

/*
One `IssuesCard`: the block's title and total, its description, then a label/count/% table
whose labels are drawn as the page's badges. Returns its height, and where its border needs
closing, so the pair of cards sharing the row can be squared off. `block.data` only contains
labels with 1+ matching claims (zero-count labels are filtered server-side), so an empty
block renders a "No issues/warnings found." message instead of an empty table.
*/
function renderIssueCard(layout, block, x, width) {
  const { doc, u } = layout;
  const padding = u(CARD_PADDING_PX);
  const top = layout.y;

  setFont(layout, TYPE.statValue);
  const valueWidth = doc.getTextWidth(count(block.count));

  let y = top + padding;

  // `stat_value` carries a top margin, which offsets it against the title beside it.
  drawText(layout, TYPE.statValue, count(block.count), x + width - padding, y + u(8), {
    align: "right",
  });

  y += drawText(layout, TYPE.bodyStrong, String(block.title || "").toUpperCase(), x + padding, y);

  y += u(4);

  y += drawText(layout, TYPE.description, block.description || "", x + padding, y, {
    width: width - padding * 2 - valueWidth - u(CONTENT_GAP_PX),
  });

  // `card__content--no-btm` drops its bottom padding; the table's own margin replaces it.
  y = Math.max(y, top + padding + u(8) + textHeight(layout, TYPE.statValue, 1)) + u(16);

  const rows = (block.data || []).map((item) => [
    String(item.label || "").toUpperCase(),
    count(item.count),
    `${item.percentage ?? 0}%`,
  ]);

  // When there are no table rows, draw the empty state and manually advance
  // `layout.y`, since `drawTable` would normally be responsible for doing that.
  if (!rows.length) {
    const message = `No ${String(block.type || "record").toLowerCase()}s found.`;

    const messageHeight = drawText(layout, TYPE.bodyMuted, message, x + padding, y);

    const endY = y + messageHeight + padding;

    layout.y = endY;

    return {
      top,
      endY,
      height: endY - top,
      segments: [],
    };
  }

  const card = cardSegments(layout, top);

  layout.y = y;

  drawTable(layout, {
    x,
    width,
    head: [[`${block.type} Type`, "Count", "% of Total Claims"]],
    body: rows,

    // `scroll-table` fixes the layout at 50% / 30% / 20%, right-aligning
    // the final percentage column.
    columnStyles: {
      0: {
        cellWidth: width * 0.5,
      },
      1: {
        cellWidth: width * 0.3,
      },
      2: {
        cellWidth: width * 0.2,
        halign: "right",
      },
    },

    hooks: {
      didParseCell: (data) => {
        // Match the header alignment to the right-aligned percentage values.
        if (data.section === "head" && data.column.index === 2) {
          data.cell.styles.halign = "right";
        }

        // The rounded card draws the outer bottom border, so remove the table's
        // bottom border from the last row to avoid it sticking past the corners.
        if (data.section === "body" && data.row.index === rows.length - 1) {
          data.cell.styles.lineWidth = {
            ...data.cell.styles.lineWidth,
            bottom: 0,
          };
        }
      },

      // Track the card across page breaks when the table spans multiple pages.
      didDrawPage: card.onPage,
    },
  });

  return {
    top,
    endY: layout.y,
    height: layout.y - top,
    segments: card.segments,
  };
}

function renderIssuesAndWarnings(layout, receipt) {
  const blocks = [receipt.issues, receipt.warnings].filter(Boolean);

  if (!blocks.length) return;

  const { margin, contentWidth, u } = layout;

  blocks.forEach((block, index) => {
    const card = renderIssueCard(layout, block, margin, contentWidth);

    // Tables that span multiple pages need a card border drawn for each segment.
    // Otherwise, draw one card using the exact Y-position captured when rendering started.
    if (card.segments.length > 1) {
      drawCardSegments(layout, margin, contentWidth, card.segments);
    } else {
      drawCard(layout, margin, card.top, contentWidth, card.height);
    }

    // Keep issues and warnings stacked vertically with a 20px gap between cards.
    if (index < blocks.length - 1) {
      layout.y += u(20);
    }
  });
}

function issuesHeight(layout, receipt) {
  const rows = Math.max(receipt.issues?.data?.length || 0, receipt.warnings?.data?.length || 0);
  const header = layout.u(CARD_PADDING_PX * 2 + 16) + textHeight(layout, TYPE.statValue, 1);

  return header + tableHeight(layout, rows, { rowPadding: 18 });
}

// One `EntitySummary` per covered entity, led by its 340B ID with the entity named underneath.
function renderEntityBreakdown(layout, entities) {
  (entities || []).forEach((entity) => {
    renderEntityCard(layout, {
      lines: [
        { token: TYPE.bodyLarge, text: entity.id_340b || "" },
        { token: TYPE.body, text: entity.name || "" },
      ],
      rowCount: entity.total_rows,
      conformingPercentage: entity.conforming_percentage,
      stats: entity.summary_counts,
      table: {
        head: [["Manufacturer", "Product", "NDC", "Conforming", "Non-Conforming"]],
        body: (entity.ndc_stats || []).map((ndc) => [
          ndc.manufacturer || "",
          ndc.product_name || "",
          ndc.ndc || "",
          count(ndc.conforming_count),
          count(ndc.non_conforming_count),
        ]),
        columnStyles: { 4: { halign: "right" } },
      },
    });
  });
}

export default async function generateReceiptPdf(receipt) {
  if (!receipt) return;

  const layout = await createDocument();
  const summary = receipt.summary || {};
  const inaccessibleRowCount = summary.claim_access_counts.inaccessible;

  renderHeader(layout, {
    eyebrow: "Submission Receipt",
    title: receipt.name,
    subtitle: `Date Submitted: ${receipt.submission_date || ""}`,
    user: receipt.submitted_by,
  });

  drawDivider(layout);
  if (inaccessibleRowCount > 0) {
    renderWarningBanner(layout, { count: inaccessibleRowCount });
  }
  renderStatCards(layout, summary.counts);

  drawDivider(layout, "Validation Summary", 70);
  renderValidationSummary(layout, summary);

  drawDivider(layout, "Issues & Warnings", issuesHeight(layout, receipt));
  renderIssuesAndWarnings(layout, receipt);

  if (receipt.entities?.length) {
    drawDivider(layout, "Claims By Covered Entities", 60);
    renderEntityBreakdown(layout, receipt.entities);
  }

  renderFooter(layout, receipt.name || "Submission Receipt");

  layout.doc.save(
    `${(receipt?.name || "340B_ESP_receipt").trim().replace(/\s+/g, "_")}_receipt.pdf`
  );
}
